import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import DOMPurify from "dompurify";
import api, { mediaUrl } from "../lib/api";

export default function BlogPost() {
    const { slug } = useParams();
    const [post, setPost] = useState(undefined); // undefined loading, null not found

    const load = useCallback(() => {
        api
            .get(`/blog/${slug}`)
            .then((r) => setPost(r.data))
            .catch(() => setPost(null));
    }, [slug]);

    useEffect(() => {
        load();
    }, [load]);

    const fmt = (d) =>
        new Date(d + "T00:00:00").toLocaleDateString(undefined, {
            month: "long",
            day: "numeric",
            year: "numeric",
        });

    if (post === undefined)
        return (
            <div className="flex min-h-[60vh] items-center justify-center text-amber-200/60">
                Loading…
            </div>
        );

    if (post === null)
        return (
            <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 text-amber-100/70">
                <p className="font-serif text-2xl">This note has wandered off.</p>
                <Link to="/blog" className="text-amber-300 hover:underline">
                    Back to the blog
                </Link>
            </div>
        );

    return (
        <article data-testid="blog-post" className="pb-28 pt-32">
            <div className="mx-auto w-full max-w-3xl px-6">
                <Link
                    to="/blog"
                    className="inline-flex items-center gap-2 text-sm text-amber-300/80 transition-colors hover:text-amber-200"
                >
                    <ArrowLeft className="h-4 w-4" /> All notes
                </Link>
                <p className="mt-8 text-xs uppercase tracking-[0.25em] text-amber-400/80">
                    {fmt(post.date)}
                </p>
                <motion.h1
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.55 }}
                    className="mt-3 font-serif text-4xl leading-tight tracking-tight text-amber-50 sm:text-5xl"
                >
                    {post.title}
                </motion.h1>
                {post.cover_image_url && (
                    <img
                        src={mediaUrl(post.cover_image_url)}
                        alt={post.title}
                        className="mt-8 w-full rounded-2xl object-cover ring-1 ring-amber-500/15"
                    />
                )}
                <div
                    data-testid="blog-post-body"
                    className="prose-editor blog-body mt-8 text-[1.05rem] leading-relaxed text-amber-100/85"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.body_html || "", { ADD_ATTR: ["target"] }) }}
                />
            </div>
        </article>
    );
}
